import { WindowedReturnsSeries } from './build-indexed-comparison';

// Minimum number of OVERLAPPING in-window daily returns a pair must share for
// its Pearson correlation to be meaningful. Two points always sit on a line
// (r = ±1), so anything below 3 is reported as "no value" rather than a
// spurious perfect correlation.
export const MIN_OVERLAP = 3;

export interface CorrelationCell {
  // Pearson r over the pair's overlapping in-window days, or null when there is
  // insufficient overlap (< MIN_OVERLAP) or one side has zero variance.
  r: number | null;
  // Number of overlapping days actually used.
  n: number;
}

export interface CorrelationMatrix {
  // Row/column order matches the input series order. `labels` are raw (unblurred)
  // — the view applies the shared blur logic.
  keys: string[];
  labels: string[];
  // cells[i][j] = correlation of row series i against column series j.
  cells: CorrelationCell[][];
}

// Overlapping in-window daily returns for a pair, aligned on common days and
// ordered chronologically. `xs` comes from `x`, `ys` from `y` — the scatter
// passes the column series as x and the row series as y.
export const pairedReturns = (
  x: WindowedReturnsSeries,
  y: WindowedReturnsSeries,
): { dates: number[]; xs: number[]; ys: number[] } => {
  const dates: number[] = [];
  const xs: number[] = [];
  const ys: number[] = [];
  const common = Array.from(x.returnsByDay.keys())
    .filter((ts) => y.returnsByDay.has(ts))
    .sort((a, b) => a - b);
  for (const ts of common) {
    dates.push(ts);
    xs.push(x.returnsByDay.get(ts) as number);
    ys.push(y.returnsByDay.get(ts) as number);
  }
  return { dates, xs, ys };
};

// Pearson product-moment correlation. Returns null for < MIN_OVERLAP paired
// points, or when either series has zero variance over the overlap (the
// correlation is then undefined, not 0).
export const pearson = (xs: number[], ys: number[]): number | null => {
  const n = xs.length;
  if (n < MIN_OVERLAP || ys.length !== n) return null;

  let sumX = 0;
  let sumY = 0;
  for (let i = 0; i < n; i++) {
    sumX += xs[i];
    sumY += ys[i];
  }
  const meanX = sumX / n;
  const meanY = sumY / n;

  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
  }

  const denom = Math.sqrt(sxx * syy);
  if (denom === 0) return null;
  // Clamp to [-1, 1] — floating-point drift can nudge a perfect correlation a
  // hair past the bound.
  return Math.max(-1, Math.min(1, sxy / denom));
};

// Builds the full NxN Pearson correlation matrix of the selected series' in-window
// daily returns, each pair using only its overlapping days. The diagonal is 1.00
// by definition (a series against itself).
export const buildCorrelationMatrix = (series: WindowedReturnsSeries[]): CorrelationMatrix => ({
  keys: series.map((s) => s.key),
  labels: series.map((s) => s.label),
  cells: series.map((row, i) =>
    series.map((col, j) => {
      if (i === j) {
        return { r: 1, n: row.returnsByDay.size };
      }
      // x = column series, y = row series — consistent with the scatter's axes.
      const { xs, ys } = pairedReturns(col, row);
      return { r: pearson(xs, ys), n: xs.length };
    }),
  ),
});
